import React, { createContext, useState, useEffect, useContext } from 'react';

// ============================================================================
// THEME CONTEXT
// ============================================================================
const ThemeContext = createContext();

// ============================================================================
// THEME PROVIDER COMPONENT
// ============================================================================
export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to light mode
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('fsgu-theme');
      if (savedTheme) {
        return savedTheme === 'dark';
      }
      // Check system preference
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  const [isAnimating, setIsAnimating] = useState(false);

  // ============================================================================
  // APPLY THEME TO DOCUMENT
  // ============================================================================
  useEffect(() => {
    const root = document.documentElement;
    
    if (isDarkMode) {
      root.classList.add('dark');
      localStorage.setItem('fsgu-theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('fsgu-theme', 'light');
    }
  }, [isDarkMode]);

  // ============================================================================
  // LISTEN FOR SYSTEM THEME CHANGES
  // ============================================================================
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only update if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('fsgu-theme');
      if (!savedTheme) {
        setIsDarkMode(e.matches);
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } 
    // Legacy browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // ============================================================================
  // TOGGLE THEME WITH ANIMATION
  // ============================================================================
  const toggleTheme = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setIsDarkMode(prev => !prev);
    
    // Reset animation state after transition
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  // ============================================================================
  // SET SPECIFIC THEME
  // ============================================================================
  const setTheme = (theme) => {
    if (theme === 'dark') {
      setIsDarkMode(true);
    } else if (theme === 'light') {
      setIsDarkMode(false);
    }
  };

  // ============================================================================
  // GET CURRENT THEME
  // ============================================================================
  const getTheme = () => {
    return isDarkMode ? 'dark' : 'light';
  };

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  const value = {
    isDarkMode,
    isAnimating,
    toggleTheme,
    setTheme,
    getTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// ============================================================================
// CUSTOM HOOK TO USE THEME CONTEXT
// ============================================================================
export const useTheme = () => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

// ============================================================================
// EXPORTS
// ============================================================================
export default ThemeContext;